/**
 * Toast notifications — the app's feedback channel for mutations (replaces
 * window.alert and silent failures). useToast().success/error from anywhere
 * under the provider; auto-dismisses; click to dismiss early.
 */

import { createContext, useCallback, useContext, useRef, useState } from "react";

interface Toast { id: number; kind: "success" | "error"; text: string }
interface ToastCtx {
  success: (text: string) => void;
  error: (text: string) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((kind: Toast["kind"], text: string) => {
    const id = nextId.current++;
    setToasts((t) => [...t, { id, kind, text }]);
    // Errors linger longer — people read them.
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "error" ? 7000 : 4000);
  }, []);

  const ctx: ToastCtx = {
    success: useCallback((text: string) => push("success", text), [push]),
    error: useCallback((text: string) => push("error", text), [push]),
  };

  return (
    <Ctx.Provider value={ctx}>
      {children}
      <div style={stack} aria-live="polite">
        {toasts.map((t) => (
          <button key={t.id} onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))} style={{ ...toastStyle, borderColor: t.kind === "error" ? "var(--bad)" : "color-mix(in srgb, var(--good) 50%, var(--border))" }}>
            <span style={{ color: t.kind === "error" ? "var(--bad)" : "var(--good)", fontWeight: 700, marginRight: 8 }}>{t.kind === "error" ? "✕" : "✓"}</span>
            {t.text}
          </button>
        ))}
      </div>
    </Ctx.Provider>
  );
}

/** Safe outside the provider (no-ops) so components never crash. */
export function useToast(): ToastCtx {
  return useContext(Ctx) ?? { success: () => {}, error: () => {} };
}

const stack: React.CSSProperties = {
  position: "fixed", bottom: 20, right: 20, zIndex: 300,
  display: "flex", flexDirection: "column", gap: 8, maxWidth: 380,
};
const toastStyle: React.CSSProperties = {
  textAlign: "left", background: "var(--surface)", color: "var(--text)",
  border: "1px solid var(--border)", borderRadius: 10, padding: "11px 14px",
  fontSize: 13, lineHeight: 1.45, boxShadow: "var(--shadow-pop)", cursor: "pointer",
  animation: "dd-fade-in .14s ease",
};
